import { axios } from '@/libs/api.request';
import { Response } from '@/types/request.d';
import ossAxios, { AxiosResponse } from 'axios';
import Base64 from 'js-base64';
import dayjs from 'dayjs';
import OSS from 'ali-oss';
import md5 from 'md5';
import CryptoJS from 'crypto-js';
import { OSS_KEY, CHANNEL_ENUM } from '@/constants/common';
import { getQueryString, recursionUpload, replaceAcceleratedDomain } from '@/libs/util';
import { FileType } from '@/api/chat/Material';
import { originFileName } from '@/filters';
import { isClient } from '@/libs/client';
import { v4 } from 'uuid';

// salesmartly上传桶host
// const salesmartlyOssHost = 'https://salesmartly.oss-accelerate.aliyuncs.com';
const salesmartlyOssHost = 'https://mix-ads.oss-accelerate.aliyuncs.com';

// 参考 https://alidocs.dingtalk.com/i/nodes/YndMj49yWjagRlxLIrb7LeQ083pmz5aA
// salesmartly oss 存储桶module集合
export type OSS_MODULE = 'material' | 'broadcast' | 'team' | 'automated' | 'integration' | 'chat' | 'download' | 'speech' | 'corpus' | 'fee' | 'appstore' | 'contact';

// 上传接口请求参数
interface OSS_PARAMS {
    module: OSS_MODULE;
    module_path: string | '';
}

export type PlatFormType = 'p1' | 'd4w' | 'd4m' // 平台
/** v2 OSS 配置请求，固定要求接口返回加密后的 STS 配置和存储路径。 */
interface OSS_CONFIG_PARAMS extends OSS_PARAMS {
    btype: string, // 存储桶
    platform: PlatFormType,
    version: 'v2',
}

/** v2 OSS 配置接口返回的加密字段，解密后才能传给 OSS SDK。 */
interface OssConfigV2Response {
    sts_config: string;
    path: string;
}

interface OSS_CONFIG_RES {
    path: string;
    aliyunOSS: any;
    error?: any;
}

interface OSS_CONFIG_CACHE {
    sts_config: {
        access_key_id: string;
        access_key_secret: string;
        expiration: string;
        security_token: string;
    };
    path: string;
    effectiveTime: any;
}

// 上传配置储存
const ossConfigMap: Map<string, OSS_CONFIG_CACHE> = new Map();

export const imageUpload = (image: File) => axios.upload({
    url: 'sys/tools/image/upload',
    method: 'post',
    data: {
        image,
    },
});

export const fileUpload = (file: File, type: string, system: 'token' | 'authorize', material_id?: string, onProgress?: any) => axios.upload({
    url: 'sys/tools/file/upload',
    method: 'post',
    data: {
        file,
        type,
        system,
        material_id: material_id || '',
    },
    onProgress,
});

export interface ProxyInfo {
    proxy_type: string;
    proxy_host: string;
    proxy_port: string;
    proxy_user: string;
    proxy_password: string;
}

interface CheckProxyParams {
    proxy_config: string;
    ip?: string;
    ip_country?: string;
    ip_region?: string;
    ip_city?: string;
}

export const checkProxy = (data: CheckProxyParams) => axios.request({
    url: 'sys/tools/proxy/check-proxy',
    method: 'post',
    customTips: true,
    data,
});

export function getFileNameByPath(path: string) {
    return originFileName(path);
}

interface UploadConfigRes {
    security_token: string;
    access_key_id: string;
    access_key_secret: string;
    expiration: string;
    effectiveTime?: any;
}

// aes-ecb 解密方法
export const decryptAes = (data: string, key: string) => {
    const cryptoKey = CryptoJS.enc.Utf8.parse(key);
    const decryptStr = CryptoJS.AES.decrypt(data, cryptoKey, {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7,
    }).toString(CryptoJS.enc.Utf8);
    return decryptStr;
};

export const uploadConfig = (() => {
    let cache!: UploadConfigRes;
    return () => {
        // 后端 默认 1小时有效。前端做缓存，10分钟后仍有效则继续使用，否则刷新
        if (cache && dayjs(cache.effectiveTime).isAfter(dayjs()) && dayjs(cache.expiration).isAfter(dayjs())) {
            return Promise.resolve(cache);
        }
        return axios.request<Response<any>>({
            url: '/sys/tools/nvx/rtk9x2',
            method: 'get',
        })
            .then((res) => {
                try {
                    const decryptStr = decryptAes(res.data.data.data, OSS_KEY);
                    cache = JSON.parse(decryptStr);
                    cache.effectiveTime = dayjs().add(2, 'minutes');
                } catch (err) {
                    console.error(err);
                }
                return cache;
            });
    };
})();

// 获取平台标识
export const getPlatForm = () => {
    let platform: PlatFormType = 'p1';
    if (isClient()) {
        if (window.navigator.userAgent && window.navigator.userAgent.includes('Windows NT')) {
            platform = 'd4w';
        } else {
            platform = 'd4m';
        }
    }
    return platform;
};

/** 获取项目 OSS 配置，并将 v2 密文解密为上传和下载共用的缓存结构。 */
export const uploadSaleSmartlyConfig = (data: OSS_PARAMS) => {
    const { module, module_path } = data;
    // 缓存key值
    const key = `${module}/${module_path}`;

    if (ossConfigMap.has(key)) {
        // console.log(['获取缓存的oss上传配置', ossConfigMap.get(key)]);
        const cacheConfig = ossConfigMap.get(key);
        // 后端 默认 1小时有效。前端做缓存
        if (cacheConfig && dayjs(cacheConfig.effectiveTime).isAfter(dayjs()) && dayjs(cacheConfig.sts_config.expiration).isAfter(dayjs())) {
            console.log(dayjs(cacheConfig.effectiveTime).format('YYYY/MM/DD HH:mm:ss'), dayjs(cacheConfig.sts_config.expiration).format('YYYY/MM/DD HH:mm:ss'));
            return Promise.resolve(cacheConfig);
        }
    }

    const params: OSS_CONFIG_PARAMS = {
        ...data,
        btype: 'salesmartly',
        platform: getPlatForm(),
        version: 'v2',
    };
    if (data.module !== 'download') {
        params.btype = 'mix_ads';
    }
    return new Promise<OSS_CONFIG_CACHE>((resolve, reject) => {
        axios.request<Response<OssConfigV2Response>>({
            url: '/sys/project/project/get-oss-config',
            method: 'post',
            data: params,
        }).then((res) => {
            const cache: OSS_CONFIG_CACHE = {
                sts_config: JSON.parse(decryptAes(res.data.data.sts_config, OSS_KEY)),
                path: decryptAes(res.data.data.path, OSS_KEY),
                effectiveTime: dayjs().add(10, 'minutes').valueOf(), // 前端缓存时间
            };
            ossConfigMap.set(key, cache);
            resolve(cache);
        }).catch((err) => {
            reject(err);
        });
    });
};

/**
 * 获取 OSS 上传实例
 */
const getOSSInstance = async (usecdn?: boolean, timeout: number = 120) => {
    let defaultEndpoint: any = {
        endpoint: 'oss-accelerate.aliyuncs.com',
    };
    if (usecdn) {
        defaultEndpoint = {
            endpoint: 'https://assets-cdn.salesmartly.com',
            cname: true,
        };
    }
    const res = await uploadConfig();
    return new OSS({
        // Bucket所在地域。以华东1（杭州）为例，Region填写为oss-cn-hangzhou。
        region: 'oss-ap-southeast-1',
        // 从STS服务获取的临时访问凭证。临时访问凭证包括临时访问密钥（AccessKeyId和AccessKeySecret）和安全令牌（SecurityToken）。
        accessKeyId: res.access_key_id,
        accessKeySecret: res.access_key_secret,
        stsToken: res.security_token,
        // 填写Bucket名称。
        bucket: 'mix-ads',
        timeout: timeout * 1000,
        ...defaultEndpoint,
    });
};

/**
 * 获取新版本 OSS 上传实例
 */
const getSaleSmartlyOSSInstance = async (data: OSS_PARAMS, timeout: number = 120, usecdn: boolean = false): Promise<OSS_CONFIG_RES> => {
    try {
        let defaultEndpoint: any = {
            endpoint: 'oss-accelerate.aliyuncs.com',
        };

        if (usecdn) {
            defaultEndpoint = {
                // endpoint: 'https://static.salesmartly.com',
                endpoint: 'https://assets-cdn.salesmartly.com',
                cname: true,
            };
        }
        const config: any = await uploadSaleSmartlyConfig(data);
        return {
            path: config.path,
            aliyunOSS: new OSS({
                // Bucket所在地域。以华东1（杭州）为例，Region填写为oss-cn-hangzhou。
                region: 'oss-ap-southeast-1',
                // 从STS服务获取的临时访问凭证。临时访问凭证包括临时访问密钥（AccessKeyId和AccessKeySecret）和安全令牌（SecurityToken）。
                accessKeyId: config.sts_config.access_key_id,
                accessKeySecret: config.sts_config.access_key_secret,
                stsToken: config.sts_config.security_token,
                // 填写Bucket名称。
                // bucket: 'salesmartly',
                bucket: data.module === 'download' ? 'salesmartly' : 'mix-ads',
                timeout: timeout * 1000,
                ...defaultEndpoint,
            }),
        };
    } catch (error) {
        return { path: '', aliyunOSS: null, error };
    }
};

export function getAssetsUrl(url: string) {
    return url.replace('mix-ads.oss-accelerate.aliyuncs.com', 'assets-cdn.salesmartly.com')
        .replace('salesmartly.oss-accelerate.aliyuncs.com', 'static.salesmartly.com')
        .replace('http://', 'https://');
}

/**
 * 根据链接规则解码下载链接
 * @param url 下载链接
 */
const decodeDownloadURL = (url: string) => {
    if (!url) {
        return '';
    }
    const urlInfo = url.split('/');

    // // p1,a2a... 是前端项目上传的文件才需要对应转码
    // if (
    //     (urlInfo[1] === 'project' && ['pc0', 'p1', 'a2a', 'a2i', 'w3', 'h5', 'd4w', 'd4m'].includes(urlInfo[3])) ||
    //     (urlInfo[0] === 'whatsapp' && (urlInfo[1] === 'ycloud' || urlInfo[1] === 'chatapp'))
    // ) {
    //     url = decodeURIComponent(url);
    // }

    // 统一去除包含key&time参数（2025-10-14 发现oss下载时，具有这两个参数获取到的下载地址无法下载）
    const time = getQueryString('time', url);
    const key = getQueryString('key', url);
    // console.log([url, time, key]);
    if (time && key) {
        url = url.split('?')[0];
        url = decodeURIComponent(url);
    } else if (!['telegram_client', 'whatsapp_go_client'].includes(urlInfo[0])) {
        // 对特定的渠道不进行解码
        url = decodeURIComponent(url);
    }

    return url;
};

// 获取oss资源下载地址
export const getOSSDownloadUrl = async (url: string, fileName: string) => {
    url = replaceAcceleratedDomain(url);

    if (url.includes('assets-cdn.salesmartly.com') || url.includes('static-cdn.salesmartly.com')) {
        const aliyunOSS = await getOSSInstance();
        // 配置响应头实现通过URL访问时自动下载文件，并设置下载后的文件名。
        const response = {
            'content-disposition': `attachment; filename=${encodeURIComponent(fileName)}`, // 自定义下载后的文件名。
        };
        // 填写Object完整路径。Object完整路径中不能包含Bucket名称。
        url = url.replace(/^http(s)?:\/\/assets-cdn\.salesmartly\.com\//, '').replace(/^http(s)?:\/\/static-cdn\.salesmartly\.com\//, '');
        url = decodeDownloadURL(url);
        return aliyunOSS.signatureUrl(url, { response });
    }

    if (url.includes('static.salesmartly.com')) {
        const { aliyunOSS } = await getSaleSmartlyOSSInstance({
            module: 'download',
            module_path: '',
        });
        // 配置响应头实现通过URL访问时自动下载文件，并设置下载后的文件名。
        const response = {
            'content-disposition': `attachment; filename=${encodeURIComponent(fileName)}`, // 自定义下载后的文件名。
        };
        // 填写Object完整路径。Object完整路径中不能包含Bucket名称。
        url = url.replace(/^http(s)?:\/\/static\.salesmartly\.com\//, '');
        url = decodeDownloadURL(url);
        return aliyunOSS.signatureUrl(url, { response });
    }

    return url;
};

export interface uploadSaleSmartlyParams {
    file: File,
    type: FileType,
    module: OSS_MODULE,
    channel: Chat.channel | '',
    timeout?: number,
    usecdn?: boolean,
    urlMaxLength?: number, // 上传url长度限制，默认为0不限制长度
    fileName?: string, // 自定义文件名
}

// 获取素材库上传实例
export const uploadOSSForSaleSmartly = async (uploadParams: uploadSaleSmartlyParams) => {
    const {
        file,
        type,
        module,
        channel,
        timeout = 120,
        usecdn = false,
        urlMaxLength = 0,
        fileName = '',
    } = uploadParams;
    // 渠道路径
    const channelPath = channel ? `${CHANNEL_ENUM[channel]}/` : '';
    const data: OSS_CONFIG_RES = await getSaleSmartlyOSSInstance({
        module,
        module_path: `${channelPath}${type}`,
    }, timeout, usecdn);
    if (data && data.aliyunOSS) {
        const { aliyunOSS, path: ossPath } = data as OSS_CONFIG_RES;
        let file_name = fileName || file.name;
        // 针对图片进行 encodeURIComponent 转义（某些渠道发送中文名称图片会有问题，例如messenger）
        if (type === 'image' && !fileName) {
            const fileSuffix = file.name.split('.').pop()!.toLocaleLowerCase();
            const dateStr = Date.now().toString();
            const uuid = v4();
            const beforeName = `${type}_${dateStr}_${md5(uuid)}`;
            file_name = `${beforeName.slice(0, 50 - fileSuffix.length)}.${fileSuffix}`;
            // console.log('[mediasend] dateStr', dateStr, file_name, uuid);
        }
        const path = `${ossPath}${Date.now()}/${file_name}`;
        const endPath = getAssetsUrl(`${salesmartlyOssHost}/${encodeURI(path)}`);
        if (urlMaxLength && (endPath.length > urlMaxLength)) {
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject({ reason: 'super-long', endPath, urlMaxLength, length: endPath.length });
        }
        console.log({ endPath, urlMaxLength, length: endPath.length });
        return aliyunOSS.put(path, file);
    }
    return Promise.reject(data);
};

/**
 * 通过url直传oss
 */
export const uploadOSSByUrl = async (uploadParams: uploadSaleSmartlyParams) => {
    const {
        file,
        type,
        module,
        channel,
        timeout = 120,
    } = uploadParams;

    const channelPath = channel ? `${CHANNEL_ENUM[channel]}/` : '';

    const config: any = await uploadSaleSmartlyConfig({
        module,
        module_path: `${channelPath}${type}`,
    });

    let fileName = file.name;
    // 针对图片进行 encodeURIComponent 转义
    if (type === 'image') {
        const fileSuffix = file.name.split('.').pop()!.toLocaleLowerCase();
        const dateStr = Date.now().toString();
        const uuid = v4();
        const beforeName = `${type}_${dateStr}_${md5(uuid)}`;
        fileName = `${beforeName.slice(0, 50 - fileSuffix.length)}.${fileSuffix}`;
    }

    const urlFileName = encodeURIComponent(fileName); // 如果中文则需要编码

    const path = `${config.path}${Date.now()}`;

    const policyText = {
        expiration: config.sts_config.expiration, // 设置policy过期时间。
        conditions: [
            // 限制上传大小。
            ['content-length-range', 0, 1024 * 1024 * 1024],
        ],
    };
    const policy = Base64.Base64.encode(JSON.stringify(policyText)); // policy必须为base64的string。
    const signature = CryptoJS.enc.Base64.stringify(CryptoJS.HmacSHA1(policy, config.sts_config.access_key_secret));

    const params = new FormData();
    params.append('signature', signature);
    params.append('ossAccessKeyId', config.sts_config.access_key_id);
    params.append('policy', policy);
    params.append('x-oss-security-token', config.sts_config.security_token);
    params.append('key', `${path}/${fileName}`);
    params.append('file', file);

    return new Promise((resolve, reject) => {
        ossAxios.post(salesmartlyOssHost, params, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            timeout: timeout * 1000,
        }).then((resp: AxiosResponse) => {
            if (resp && resp.status === 204) {
                resolve({
                    url: getAssetsUrl(`${salesmartlyOssHost}/${path}/${urlFileName}`),
                });
            } else {
                reject(resp);
            }
        }).catch((err) => {
            reject(err);
        });
    });
};

/**
 * 拥有重试机制的上传方法
 */
export const uploadSaleSmartlyOSSByRetry = (uploadParams: uploadSaleSmartlyParams) => {
    // 是否柬埔寨IP，柬埔寨IP不开启重试
    // await store.dispatch('main/getVisitorIpInfo');
    // const isCambodiaIP = store.getters['main/isCambodiaIP'];
    // console.log('是否柬埔寨IP：', isCambodiaIP);

    // 不是柬埔寨地区的项目使用重试机制
    // const isRetry = !isCambodiaIP;
    // console.log('是否开启重试：', isRetry);
    // if (!isRetry) {
    //     // 不开启则默认采用之前的上传方法
    //     return uploadOSSForSaleSmartly(uploadParams);
    // }

    const triggerCondition = [
        // 网络错误
        { name: 'RequestError', status: -1, code: 'RequestError' },
        // 请求超时
        { name: 'ConnectionTimeoutError', status: -2, code: 'ConnectionTimeoutError' },
    ];

    // 配置重试方法
    let config = [{
        fn: uploadOSSForSaleSmartly,
        errInfo: triggerCondition,
        params: uploadParams,
    }, {
        fn: uploadOSSForSaleSmartly,
        errInfo: null,
        params: uploadParams,
    }, {
        fn: uploadOSSByUrl,
        errInfo: null,
        params: uploadParams,
    }, {
        fn: uploadOSSForSaleSmartly,
        errInfo: null,
        params: { ...uploadParams, usecdn: true },
    }];

    if (uploadParams.file.size < 1024 * 1024 * 2 && uploadParams.type === 'image') {
        console.log('-----上传文件为2M以下，改变上传方法顺序-----', uploadParams.file.size / 1024);
        // 2M以下改变上传策略
        config = [{
            fn: uploadOSSForSaleSmartly,
            errInfo: triggerCondition,
            params: { ...uploadParams, usecdn: true },
        }, {
            fn: uploadOSSForSaleSmartly,
            errInfo: null,
            params: { ...uploadParams, usecdn: true },
        }, {
            fn: uploadOSSByUrl,
            errInfo: null,
            params: uploadParams,
        }, {
            fn: uploadOSSForSaleSmartly,
            errInfo: null,
            params: uploadParams,
        }];
    }

    // 递归调用上传函数
    return recursionUpload(config, uploadParams.file, true);
};

/**
 * 团队管理上传成员头像
 */
export const uploadOSSForTeamAvatar = async ({ file }: { file: File }) => {
    return uploadOSSForSaleSmartly({
        file,
        type: 'image',
        module: 'team',
        channel: '',
    });
};

/**
 * 集成-插件上传头像/自定义图标
 */
export const uploadOSSForSetting = async ({ file }: { file: File }) => {
    return uploadOSSForSaleSmartly({
        file,
        type: 'image',
        module: 'integration',
        channel: '2',
    });
};

/**
 * 集成-插件上传自定义图标
 */
export const uploadOSSForIcon = async ({ file }: { file: File }) => {
    return uploadOSSForSaleSmartly({
        file,
        type: 'image',
        module: 'integration',
        channel: '2',
    });
};

/**
 * 集成-微信客服上传头像
 */
export const uploadOSSForWeixin = async ({ file }: { file: File }) => {
    return uploadOSSForSaleSmartly({
        file,
        type: 'image',
        module: 'integration',
        channel: '11',
    });
};

/**
 * 富文本粘贴图片上传
 */
export const uploadOSSForEmail = async ({ file, fileName, channel, module }: { file: File; fileName: string; channel: Chat.channel | '', module: OSS_MODULE }) => {
    const name = fileName || Date.now().toString();
    const _file_format = name
        .split('.')
        .pop()!
        .toLocaleLowerCase();
    return uploadOSSForSaleSmartly({
        file,
        type: 'image',
        module,
        channel,
        fileName: `${md5(fileName)}.${_file_format}`,
    });
};

/**
 * EDM代发上传附件
 */
export const uploadOSSForEmailAttachments = async ({ file }: { file: File }) => {
    return uploadOSSForSaleSmartly({
        file,
        type: 'document',
        module: 'broadcast',
        channel: '3',
    });
};

/**
 * WhatsApp API 模版上传素材
 */
export const uploadOSSForWsTpl = async ({ file, type }: { file: File, type: FileType }) => {
    return uploadOSSForSaleSmartly({
        file,
        type,
        module: 'broadcast',
        channel: '7',
    });
};

/**
 *  集成-Messenger&评论 设置欢迎语
 */
export const uploadOSSForWelcomeMsg = async ({ file }: { file: File }) => {
    return uploadOSSForSaleSmartly({
        file,
        type: 'image',
        module: 'integration',
        channel: '1',
    });
};

/**
 * 集成- WhatsApp API 修改号码资料上传头像
 */
export const uploadOSSForWhatsApp = async ({ file }: { file: File}) => {
    return uploadOSSForSaleSmartly({
        file,
        type: 'image',
        module: 'integration',
        channel: '7',
    });
};

/**
 * 费用中心- 应用商店/费用中心 上传对公回执
 */
export const uploadOSSForFee = async ({ file }: { file: File}) => {
    return uploadOSSForSaleSmartly({
        file,
        type: 'image',
        module: 'fee',
        channel: '',
    });
};

interface GetThirdPartyLoginAppIdData {
    FACEBOOK_LOGIN_ID: {
        appid: string;
        config_id: string;
    };
    TELEGRAM_LOGIN_ID: {
        appid: string;
    };
}

/**
 * 获取tg，fb登陆注册组件id
 * @param params
 */
export const getThirdPartyLoginAppId = (params:{keys: string}) => axios.request<Response<GetThirdPartyLoginAppIdData>>({
    url: '/sys/tools/tools/get-login-config',
    method: 'get',
    params,
});

export interface GetSecureSessionIdData {
    encrypted_session_key: string;
}

export interface GetSecureSessionIdRes {
    encryption_session_id: string;
    expires_in: string;
}

export const getSecureSessionId = (data: GetSecureSessionIdData) => axios.request<Response<GetSecureSessionIdRes>>({
    url: '/sys/tools/session/get-session-id',
    method: 'post',
    data,
    customTips: true,
});
